import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Search, Users } from 'lucide-react'
import { useTeams } from '@/features/teams/hooks'
import type { Team } from '@/types/entities'

interface UserTeamsSelectionDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    selectedTeamIds: number[]
    primaryTeamId?: number | null
    onSelectionChange: (teamIds: number[], primaryTeamId?: number | null) => void
    disabled?: boolean
}

export function UserTeamsSelectionDialog({
    open,
    onOpenChange,
    selectedTeamIds,
    primaryTeamId,
    onSelectionChange,
    disabled = false,
}: UserTeamsSelectionDialogProps) {
    const { t } = useTranslation()
    const { data: teamsResponse } = useTeams()
    const availableTeams = teamsResponse?.data || []
    const [searchQuery, setSearchQuery] = useState('')
    const [localSelection, setLocalSelection] = useState<number[]>(selectedTeamIds)
    const [localPrimaryTeamId, setLocalPrimaryTeamId] = useState<number | null>(primaryTeamId || null)

    // Update local selection when selectedTeamIds prop changes
    useEffect(() => {
        setLocalSelection(selectedTeamIds)
        setLocalPrimaryTeamId(primaryTeamId || null)
    }, [selectedTeamIds, primaryTeamId, open])

    // Filter teams based on search query
    const filteredTeams = availableTeams.filter((team) => {
        const query = searchQuery.toLowerCase()
        return (
            team.teamName.toLowerCase().includes(query) ||
            (team.description && team.description.toLowerCase().includes(query))
        )
    })

    const handleToggleTeam = (teamId: number) => {
        if (disabled) return
        
        setLocalSelection((prev) => {
            if (prev.includes(teamId)) {
                // If removing the primary team, set a new primary if other teams exist
                if (localPrimaryTeamId === teamId) {
                    const remainingTeams = prev.filter((id) => id !== teamId)
                    if (remainingTeams.length > 0) {
                        setLocalPrimaryTeamId(remainingTeams[0])
                    } else {
                        setLocalPrimaryTeamId(null)
                    }
                }
                return prev.filter((id) => id !== teamId)
            } else {
                const newSelection = [...prev, teamId]
                // If no primary is selected, automatically set the first selected team as primary
                if (!localPrimaryTeamId && newSelection.length === 1) {
                    setLocalPrimaryTeamId(teamId)
                }
                return newSelection
            }
        })
    }

    const handlePrimaryTeamChange = (teamId: string) => {
        if (disabled) return
        setLocalPrimaryTeamId(teamId ? parseInt(teamId) : null)
    }

    const handleSelectAll = () => {
        if (disabled) return
        const allTeamIds = filteredTeams.map((team) => team.id)
        setLocalSelection(allTeamIds)
        // If no primary is set, set the first team as primary
        if (!localPrimaryTeamId || !allTeamIds.includes(localPrimaryTeamId)) {
            setLocalPrimaryTeamId(allTeamIds[0] || null)
        }
    }

    const handleDeselectAll = () => {
        if (disabled) return
        setLocalSelection([])
        setLocalPrimaryTeamId(null)
    }

    const handleSave = () => {
        // Validate: must have at least one team selected
        if (localSelection.length === 0) {
            // This should be caught by form validation, but show error if somehow reached
            return
        }
        
        // Ensure primary team is set - if not set or invalid, use first team
        let primaryId = localPrimaryTeamId
        if (!primaryId || !localSelection.includes(primaryId)) {
            // Use first selected team as primary if none selected or invalid
            primaryId = localSelection[0]
        }
        
        onSelectionChange(localSelection, primaryId)
        onOpenChange(false)
    }

    const handleCancel = () => {
        setLocalSelection(selectedTeamIds) // Reset to original selection
        setLocalPrimaryTeamId(primaryTeamId || null)
        onOpenChange(false)
    }

    const selectedCount = localSelection.length
    const filteredSelectedCount = filteredTeams.filter((team) =>
        localSelection.includes(team.id)
    ).length

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>{t('users.selectTeams')}</DialogTitle>
                    <DialogDescription>
                        {t('users.selectTeamsDescription')}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col flex-1 min-h-0 space-y-4">
                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder={t('users.searchTeams')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9"
                        />
                    </div>

                    {/* Selection actions */}
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-muted-foreground">
                            {t('users.selectedTeamsCount', { count: selectedCount })}
                            {searchQuery && (
                                <span className="ml-2">
                                    ({t('users.filteredSelectedCount', { count: filteredSelectedCount, total: filteredTeams.length })})
                                </span>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleSelectAll}
                                disabled={disabled || filteredTeams.length === 0}
                            >
                                {t('users.selectAll')}
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleDeselectAll}
                                disabled={disabled || filteredSelectedCount === 0}
                            >
                                {t('users.deselectAll')}
                            </Button>
                        </div>
                    </div>

                    {/* Teams list */}
                    <div className="flex-1 overflow-y-auto border rounded-md min-h-[300px] max-h-[400px]">
                        {filteredTeams.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-8 text-center p-4">
                                <Users className="h-12 w-12 text-muted-foreground mb-2" />
                                <p className="text-sm text-muted-foreground">
                                    {searchQuery
                                        ? t('users.noTeamsFound')
                                        : t('users.noTeamsAvailable')}
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="sticky top-0 bg-background border-b z-10">
                                        <tr>
                                            <th className="text-left p-3 text-sm font-medium w-12">
                                                <span className="sr-only">{t('users.select')}</span>
                                            </th>
                                            <th className="text-left p-3 text-sm font-medium">
                                                {t('users.team')}
                                            </th>
                                            <th className="text-left p-3 text-sm font-medium w-24">
                                                {t('users.primary')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredTeams.map((team) => {
                                            const isSelected = localSelection.includes(team.id)
                                            const isPrimary = localPrimaryTeamId === team.id
                                            const checkboxId = `team-${team.id}`
                                            const radioId = `primary-${team.id}`
                                            return (
                                                <tr
                                                    key={team.id}
                                                    className="border-b hover:bg-accent transition-colors"
                                                    onClick={(e) => {
                                                        // Prevent row click from interfering with radio button
                                                        if ((e.target as HTMLElement).closest('input[type="radio"]')) {
                                                            return
                                                        }
                                                    }}
                                                >
                                                    <td className="p-3">
                                                        <Checkbox
                                                            id={checkboxId}
                                                            checked={isSelected}
                                                            onCheckedChange={() => handleToggleTeam(team.id)}
                                                            disabled={disabled}
                                                        />
                                                    </td>
                                                    <td className="p-3">
                                                        <Label
                                                            htmlFor={checkboxId}
                                                            className="cursor-pointer"
                                                        >
                                                            <div className="font-medium">{team.teamName}</div>
                                                            {team.description && (
                                                                <div className="text-sm text-muted-foreground mt-1">
                                                                    {team.description}
                                                                </div>
                                                            )}
                                                        </Label>
                                                    </td>
                                                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                                                        {isSelected ? (
                                                            <div className="flex items-center justify-center">
                                                                <input
                                                                    type="radio"
                                                                    id={radioId}
                                                                    name="primaryTeam"
                                                                    value={team.id.toString()}
                                                                    checked={isPrimary}
                                                                    onChange={(e) => {
                                                                        e.stopPropagation()
                                                                        if (!disabled) {
                                                                            setLocalPrimaryTeamId(team.id)
                                                                        }
                                                                    }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        if (!disabled) {
                                                                            setLocalPrimaryTeamId(team.id)
                                                                        }
                                                                    }}
                                                                    disabled={disabled}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted-foreground text-sm">-</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer actions */}
                <div className="flex justify-end space-x-2 pt-4 border-t">
                    <Button variant="outline" onClick={handleCancel} disabled={disabled}>
                        {t('userForm.cancel')}
                    </Button>
                    <Button onClick={handleSave} disabled={disabled}>
                        {t('common.save')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}
